/**
 * Assessment service implementation
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 07/03/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author        | Task                | Comments |
 * 1      07/03/2025    Akhil Soman       Creating new File
 */

import api from "./api.ts";

class AssessmentService {
  /**
   * Create a new assessment
   * @param url - API endpoint
   * @param data - Assessment data to create
   * @returns Promise with the created assessment data
   */
  async createAssessment(url: string, data: any) {
    try {
      const response = await api.post(url, data);
      if (response.status === 200) {
        return response; // Return the relevant data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error creating assessment:", error);
      throw error;
    }
  }

  /**
   * Create a new assessment
   * @param url - API endpoint
   * @param data - Assessment data to create
   * @returns Promise with the created assessment data
   */
  async createAssessmentByFile(url: string, data: any) {
    try {
      const response = await api.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        return response; // Return the relevant data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error creating assessment:", error);
      throw error;
    }
  }

  /**
   * Update an existing assessment
   * @param url - API endpoint
   * @param data - Assessment data to update
   * @returns Promise with the updated assessment data
   */
  async updateAssessment(url: string, data: any) {
    try {
      const response = await api.put(url, data);
      if (response.status === 200) {
        return response.data?.Questions; // Return the relevant data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating assessment:", error);
      throw error;
    }
  }

  /**
   * Get assessment by email
   * @param url - API endpoint
   * @param data - Email data to fetch assessment
   * @returns Promise with the assessment data
   */
  async getAssessmentByEmail(url: string, data: any) {
    try {
      const queryParams = new URLSearchParams(data).toString();
  
      const urlWithParams = `${url}?${queryParams}`;
  
      const response = await api.get(urlWithParams);
  
      if (response.status === 200) {
        return response.data; 
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assessment by email:", error);
      throw error;
    }
  }
  

  /**
   * Get All assessments
   * @param url - API endpoint
   * @returns Promise with the assessment data
   */
  async getAssessments(url: string, params: { page: number; limit: number }) {
    try {
      const response = await api.get(url, {
        params: {
          page: params.page,
          perPage: params.limit,
        },
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assessments: ", error);
      throw error;
    }
  }

  /**
   * Get assessment data by ID
   * @param url - API endpoint
   * @param assessmentId - ID of the assessment to fetch
   * @returns Promise with the assessment data
   */
  async getAssessmentDataByID(url: string, assessmentId: string) {
    try {
      const response = await api.get(url, {
        params: { assessmentId }, // Pass assessmentId as a query parameter
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assessment by ID:", error);
      throw error;
    }
  }

  /**
   * Submit assessment
   * @param url - API endpoint
   * @param data - Assessment data to submit
   * @returns Promise with the submitted assessment data
   */
  async submitAssessmentDataByID(url: string, data: any) {
    try {
      const response = await api.put(url, data);
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assessment by ID:", error);
      throw error;
    }
  }

  /**
   * Assessment Summary
   * @param url - API endpoint
   * @param assessmentId - ID of the assessment to fetch
   * @param userId - ID of the userId to fetch
   * @returns Promise with the summary assessment data
   */
  async getAssessmentSummaryData(url: string, assessmentId: any, userId: any) {
    try {
      const response = await api.get(url, {
        params: { assessmentId, userId }, // Pass assessmentId as a query parameter
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching assessment summary:", error);
      throw error;
    }
  }
  /**
   * Send an email notification
   * @param url - API endpoint for sending emails
   * @param email - Recipient email
   * @param eventType - Type of email event (e.g., registration_successful)
   * @returns Promise with the response
   */
  async sendEmailNotification(url: string, email: string, eventType: string) {
    try {
      const response = await api.post(url, { email, eventType });
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      throw error;
    }
  }

  /**
   * Update certificate download status
   * @param url - API endpoint for updating download status
   * @param userEmail - User's email address
   * @param assessmentId - ID of the assessment/certificate
   * @returns Promise with the response data
   */
  async updateCertificateDownloadStatus(
    url: string,
    AssessmentID: string
  ) {
    try {
      const response = await api.put(url, { AssessmentID });
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating certificate download status:", error);
      throw error;
    }
  }
}

const assessmentService = new AssessmentService();
export default assessmentService;
